(function () {
  if (customElements.get('range-cans-collection')) return;

  const esc = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  customElements.define('range-cans-collection', class RangeCansCollection extends HTMLElement {
    connectedCallback() {
      this.bindQuickAdd();
      this.nav = this.querySelector('[data-crc-nav]');
      this.chips = this.querySelector('[data-crc-nav-chips]');
      this.links = Array.from(this.querySelectorAll('[data-crc-nav-link]'));
      // Scrollspy targets are whatever the chips point at: family bands and first-of-caliber cards.
      this.groups = this.links.map((link) => document.getElementById(link.dataset.crcNavLink)).filter(Boolean);
      if (!this.nav) return;

      // The theme header section carries the sticky/hidden state classes; sticky-header carries data-scroll-up.
      this.headerSection = document.querySelector('.shopify-section-header') || document.querySelector('.section-header');
      this.stickyHeader = document.querySelector('sticky-header');
      this.onScroll = this.onScroll.bind(this);
      this.syncTop = this.syncTop.bind(this);
      this.syncTop();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      if (this.headerSection) {
        // Re-sync the instant the header changes state, and again once its slide finishes.
        this.headerObserver = new MutationObserver(this.syncTop);
        this.headerObserver.observe(this.headerSection, { attributes: true, attributeFilter: ['class'] });
        this.headerSection.addEventListener('transitionend', this.syncTop);
      }
      // Sentinel just above the nav: flips the shadow without depending on scroll timing.
      if ('IntersectionObserver' in window) {
        this.stuckObserver = new IntersectionObserver(() => this.syncStuck(), { threshold: [0, 1], rootMargin: '-1px 0px 0px 0px' });
        this.stuckObserver.observe(this.nav);
      }

      this.links.forEach((link) => {
        link.addEventListener('click', (event) => {
          const target = document.getElementById(link.dataset.crcNavLink);
          if (!target) return;
          event.preventDefault();
          const targetTop = target.getBoundingClientRect().top + window.scrollY;
          const goingDown = targetTop > window.scrollY;
          // Predict where the header will be once the scroll lands, not where it is mid-animation.
          const headerAfter = goingDown && this.headerHidesOnScroll() ? 0 : this.headerHeight();
          const top = Math.max(0, targetTop - headerAfter - this.nav.offsetHeight - 8);
          // Cards in one grid row share a top edge: keep the tapped chip lit until the shopper scrolls.
          this.pinned = target.id;
          window.scrollTo({ top, behavior: reduceMotion() ? 'auto' : 'smooth' });
          history.replaceState(null, '', `#${target.id}`);
          // Same-row card already in place: no scroll event fires, so update the highlight now.
          this.onScroll();
        });
      });

      // Any scroll the shopper starts releases the tapped chip.
      const unpin = () => { this.pinned = null; };
      ['wheel', 'touchstart', 'keydown'].forEach((type) => window.addEventListener(type, unpin, { passive: true }));

      this.onScroll();
    }

    loadData() {
      if (this.data !== undefined) return this.data;
      const node = document.querySelector('[data-crc-products]');
      try { this.data = node ? JSON.parse(node.textContent) : null; } catch (e) { this.data = null; }
      return this.data;
    }

    bindQuickAdd() {
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-crc-quick]');
        if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
        if (trigger.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
        const data = this.loadData();
        const product = data && data.products.find((p) => p.handle === trigger.dataset.crcQuick);
        if (!product) return;
        event.preventDefault();
        this.openQuickAdd(product, trigger.dataset.variantId, trigger);
      });
    }

    familyOf(product) {
      const families = (this.loadData().families || []).map((f) => f.split(',').map((t) => t.trim().toLowerCase()));
      const tags = product.tags.map((t) => t.toLowerCase());
      return families.findIndex((f) => f.some((t) => t && tags.includes(t)));
    }

    suggestionsFor(product, limit = 3) {
      const data = this.loadData();
      const fam = this.familyOf(product);
      const others = data.products.filter((p) => p.handle !== product.handle && p.available && p.variants[0] && p.variants[0].available);
      const ranked = [...others.filter((p) => this.familyOf(p) === fam), ...others.filter((p) => this.familyOf(p) !== fam)];
      return ranked.slice(0, limit).map((p) => ({
        title: p.title, image: p.image, variantId: p.variants[0].id, price: p.variants[0].price, cents: p.variants[0].cents, label: p.variants[0].title
      }));
    }

    async rangeCanCount() {
      const data = this.loadData();
      const handles = new Set((data && data.products || []).map((p) => p.handle));
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('cart');
      const cart = await res.json();
      const cans = cart.items.reduce((n, item) => n + (handles.has(item.handle) || (item.properties && item.properties.Package) ? item.quantity : 0), 0);
      return { cans, total: cart.item_count };
    }

    /* The UpCart app patches window.fetch and slides its drawer open on every /cart/add.js.
       A same-origin blank iframe has its own, unpatched fetch, so the quick-add sheet stays
       the only thing on screen. Falls back to window.fetch if the iframe is unavailable. */
    cartFetch(path, options) {
      let f = window.fetch;
      try {
        if (!this.cleanFrame || !this.cleanFrame.isConnected) {
          this.cleanFrame = document.createElement('iframe');
          this.cleanFrame.hidden = true;
          this.cleanFrame.setAttribute('aria-hidden', 'true');
          this.cleanFrame.tabIndex = -1;
          document.body.appendChild(this.cleanFrame);
        }
        if (this.cleanFrame.contentWindow && this.cleanFrame.contentWindow.fetch) f = this.cleanFrame.contentWindow.fetch.bind(this.cleanFrame.contentWindow);
      } catch (e) { /* use the page fetch */ }
      return f(location.origin + path, options);
    }

    /* Second-can offers after an add: one source of truth in RangeCanUpsell.offers
       (assets/range-can-upsell.js), shared with the product pages. */
    offersFor(product, pickedVariant, isStock) {
      if (!window.RangeCanUpsell || !window.RangeCanUpsell.offers) return [];
      return window.RangeCanUpsell.offers(product, this.loadData().products, pickedVariant, isStock);
    }

    updateCartBubble(total) {
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((el) => { el.textContent = total; });
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    openQuickAdd(product, variantId, opener) {
      const data = this.loadData();
      const pairOn = Boolean(data && data.pairDiscount);
      const [range, stock] = product.variants;
      const requested = product.variants.find((v) => String(v.id) === String(variantId));
      const initial = requested && requested.available ? requested : product.variants.find((v) => v.available);
      const suggestions = pairOn ? this.suggestionsFor(product, window.innerWidth < 750 ? 1 : 2) : [];
      const shortTitle = String(product.title || '').replace(/ Loaded Range Can$/, '').replace(/ Range Can$/, '').replace(/ Can$/, '');
      const money = (cents) => `$${(Math.round(cents) / 100).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
      const countLine = (v) => (v.count ? `${Number(v.count).toLocaleString('en-US')} ${esc(v.unit || 'rds')}` : `${esc(v.title.split(' ')[0].replace('-', ' ').toLowerCase())}s`);
      // One short line per tier: rounds, cans, targets. Same order on both so they compare at a glance.
      const tierCopy = (v, i) => (i === 1
        ? `<b class="crc-qa__qty">${countLine(v)}</b> &middot; 2 engraved cans &middot; more targets`
        : `<b class="crc-qa__qty">${countLine(v)}</b> &middot; 1 engraved can &middot; targets`);
      const helpFor = (isStock) => (isStock ? 'Optional. Engraved on both cans. Check the spelling.' : 'Optional. Engraved under the design. Check the spelling.');
      const returnFocus = opener || document.activeElement;
      // Reuse the card photo the shopper is already looking at, so the sheet never paints an empty thumb.
      const cardImg = opener && opener.closest && opener.closest('.crc-card') && opener.closest('.crc-card').querySelector('.crc-card__img');
      const thumb = (cardImg && (cardImg.currentSrc || cardImg.src)) || product.image;
      const trustBits = String((data && data.trust) || '').split('|').map((t) => t.trim()).filter((t) => t && !/veteran/i.test(t));
      if (data && data.phone) trustBits.push(`Questions? ${data.phone}`);
      const trustLine = trustBits.length ? `<p class="crc-qa__trust">${trustBits.map((t) => `<span>${esc(t)}</span>`).join('')}</p>` : '';
      const NAME_OK = /^[A-Z0-9 .,'&-]*$/;
      const saveOn = (cents) => `Saves ${money(Math.floor((cents * 0.05) / 100) * 100)} with 5% off`;
      // Opened from the picker's Engrave + add (pa-rl-range-cans): the package was just chosen on the page, so the
      // sheet confirms it on one line with Change instead of asking again. The radios stay in the form, hidden.
      const fromPanel = Boolean(opener && opener.hasAttribute && opener.hasAttribute('data-pa-rc-add') && initial);
      const initialIndex = initial ? product.variants.indexOf(initial) : -1;
      const pickedLine = fromPanel ? `
            <div class="crc-qa__picked" data-qa-picked>
              <span class="crc-qa__picked-what"><b>${initialIndex === 1 ? 'Stock-Up Can' : 'Range Day Can'}</b>${tierCopy(initial, initialIndex)}</span>
              <span class="crc-qa__picked-price">${esc(initial.price)}</span>
              <button type="button" class="crc-qa__picked-change" data-qa-change aria-label="Change package">Change</button>
            </div>` : '';

      const root = document.createElement('div');
      root.className = 'rcu crc-qa';
      root.innerHTML = `<div class="rcu__scrim" data-qa-close></div>
        <div class="rcu__panel" role="dialog" aria-modal="true" aria-labelledby="crc-qa-title">
          <button type="button" class="crc-qa__x" aria-label="Close" data-qa-close>&times;</button>
          <div class="crc-qa__view" data-qa-form>
            <div class="crc-qa__head">
              ${thumb ? `<img src="${esc(thumb)}" alt="" width="84" height="84" fetchpriority="high">` : ''}
              <div><p class="rcu__eyebrow">Quick add</p><h2 id="crc-qa-title" class="rcu__title">${esc(shortTitle)}</h2></div>
            </div>
            ${pickedLine}
            <div class="crc-qa__tiers" role="radiogroup" aria-label="Choose a size"${fromPanel ? ' hidden' : ''}>
              ${product.variants.map((v, i) => `
                <label class="crc-qa__tier${i === 1 ? ' crc-qa__tier--best' : ''}${v.available ? '' : ' is-soldout'}">
                  <input type="radio" name="crc-qa-tier" value="${esc(v.id)}" ${initial && String(v.id) === String(initial.id) ? 'checked' : ''} ${v.available ? '' : 'disabled'}>
                  ${i === 1 ? '<em>2 cans inside</em>' : ''}
                  <span><strong>${i === 1 ? 'Stock-Up Can' : 'Range Day Can'}</strong><small>${v.available ? tierCopy(v, i) : 'Sold out'}</small></span>
                  <b>${v.compare ? `<s>${esc(v.compare)}</s>` : ''}${esc(v.price)}</b>
                </label>`).join('')}
            </div>
            <p class="crc-qa__nudge" id="crc-qa-nudge" role="status" data-qa-nudge hidden><span class="crc-qa__nudge-tag">Free</span>Your name engraves free. Type it below, or tap again to add the standard can.</p>
            <label class="crc-qa__name"><span class="crc-qa__name-label">Name for the can (free)</span>
              <input type="text" maxlength="20" autocomplete="off" autocapitalize="characters" placeholder="e.g. MILLER" aria-describedby="crc-qa-name-help" data-qa-name>
              <span class="crc-qa__help" id="crc-qa-name-help"><span><span data-qa-help>${helpFor(initial && stock && String(initial.id) === String(stock.id))}</span></span><span class="crc-qa__count" aria-live="polite" data-qa-count>0/20</span></span></label>
            <p class="crc-qa__name-error" id="crc-qa-name-error" role="alert" data-qa-name-error hidden>Letters, numbers, spaces and . , ' &amp; - only.</p>
            <p class="crc-qa__ship">Ships UPS Ground in an unmarked box. Not available in AK, CA, HI, NY, DC or U.S. territories.</p>
            <div class="crc-qa__foot">
              <p class="crc-qa__error" data-qa-error role="alert" hidden></p>
              <p class="crc-qa__total" data-qa-total aria-live="polite" hidden></p>
              <button type="button" class="rcu__primary" data-qa-add${initial ? '' : ' disabled'}>${initial ? 'Add to cart' : 'Sold out'}</button>
              ${trustLine}
              <a class="rcu__secondary crc-qa__details" href="${esc(product.url)}">See full details</a>
            </div>
          </div>
          <div class="crc-qa__view crc-qa__done" data-qa-done hidden>
            <p class="rcu__eyebrow">Added to cart</p>
            <h2 class="rcu__title" data-qa-done-title tabindex="-1">${esc(shortTitle)} is in your cart</h2>
            <p class="crc-qa__engraved" data-qa-engraved></p>
            <div class="crc-qa__offers" data-qa-offers hidden>
              <p class="crc-qa__offers-head"><strong>Round out the range day: 5% off both cans.</strong> One tap adds it and takes you to checkout.</p>
              <div class="crc-qa__more-list" data-qa-more-list></div>
            </div>
            <p class="crc-qa__done-text" data-qa-done-text></p>
            <a class="rcu__primary crc-qa__checkout" href="/checkout" data-qa-checkout>Checkout <span aria-hidden="true">&rarr;</span></a>
          </div>
        </div>`;
      document.body.appendChild(root);
      document.documentElement.classList.add('rcu-open');
      requestAnimationFrame(() => root.classList.add('is-open'));

      const panel = root.querySelector('.rcu__panel');
      const onKey = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); close(); return; }
        if (e.key !== 'Tab') return;
        const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
        else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      let closed = false;
      const close = () => {
        if (closed) return;
        closed = true;
        document.removeEventListener('keydown', onKey, true);
        root.classList.remove('is-open');
        document.documentElement.classList.remove('rcu-open');
        setTimeout(() => root.remove(), 200);
        if (returnFocus && typeof returnFocus.focus === 'function') returnFocus.focus({ preventScroll: true });
      };
      document.addEventListener('keydown', onKey, true);
      root.querySelectorAll('[data-qa-close]').forEach((el) => el.addEventListener('click', close));
      // Change: back to both packages, focus on the one that's picked
      const changeBtn = root.querySelector('[data-qa-change]');
      if (changeBtn) changeBtn.addEventListener('click', () => {
        const tiers = root.querySelector('.crc-qa__tiers');
        tiers.hidden = false;
        root.querySelector('[data-qa-picked]').remove();
        const checked = tiers.querySelector('input:checked');
        if (checked) checked.focus();
      });

      const name = root.querySelector('[data-qa-name]');
      const nameCount = root.querySelector('[data-qa-count]');
      const nameError = root.querySelector('[data-qa-name-error]');
      const nameValid = () => NAME_OK.test(name.value);
      const syncNameError = () => {
        const bad = !nameValid();
        if (nameError) nameError.hidden = !bad;
        if (bad) name.setAttribute('aria-invalid', 'true'); else name.removeAttribute('aria-invalid');
        const ids = ['crc-qa-name-help'];
        if (bad) ids.unshift('crc-qa-name-error');
        if (nudged && nudge && !nudge.hidden) ids.unshift('crc-qa-nudge');
        name.setAttribute('aria-describedby', ids.join(' '));
        return !bad;
      };
      name.addEventListener('input', () => {
        const upper = name.value.replace(/[\u2018\u2019\u02BC]/g, "'").toUpperCase();
        if (upper !== name.value) {
          const s = name.selectionStart;
          const e = name.selectionEnd;
          name.value = upper;
          try { name.setSelectionRange(s, e); } catch (err) { /* caret restore is best effort */ }
        }
        if (nameCount) nameCount.textContent = `${name.value.length}/20`;
        if (nudge && name.value) { nudge.hidden = true; root.classList.remove('is-nudged'); }
        syncNameError();
        clearError();
        syncButton();
      });
      const addBtn = root.querySelector('[data-qa-add]');
      const error = root.querySelector('[data-qa-error]');
      const total = root.querySelector('[data-qa-total]');
      const help = root.querySelector('[data-qa-help]');
      const nudge = root.querySelector('[data-qa-nudge]');
      let nudged = false;
      const doubleBox = root.querySelector('[data-qa-double]');
      const doublePrice = root.querySelector('[data-qa-double-price]');
      const pickedTier = () => root.querySelector('input[name="crc-qa-tier"]:checked');
      const extras = () => Array.from(root.querySelectorAll('[data-qa-extra]:checked')).map((el) => Number(el.value));
      const cansAdding = () => {
        const picked = pickedTier();
        if (!picked) return 0;
        return 1 + (doubleBox && doubleBox.checked ? 1 : 0) + extras().length;
      };
      const details = root.querySelector('.crc-qa__details');
      const syncDetails = () => {
        const picked = pickedTier();
        if (details) details.href = picked ? `${product.url}${product.url.includes('?') ? '&' : '?'}variant=${encodeURIComponent(picked.value)}` : product.url;
      };
      if (details) {
        details.addEventListener('click', () => {
          try {
            const key = `crc-engrave-${product.handle}`;
            if (name.value.trim()) sessionStorage.setItem(key, name.value.trim());
            else sessionStorage.removeItem(key);
          } catch (e) { /* storage blocked: the PDP just opens without the name */ }
        });
      }
      const clearError = () => { if (error) error.hidden = true; };
      const syncButton = () => {
        syncDetails();
        if (addBtn.getAttribute('aria-busy') === 'true') return;
        const n = cansAdding();
        addBtn.disabled = n === 0;
        const picked = pickedTier();
        const v = picked && product.variants.find((x) => String(x.id) === picked.value);
        let subtotal = v && v.cents ? v.cents * (doubleBox && doubleBox.checked ? 2 : 1) : 0;
        extras().forEach((id) => {
          const sug = suggestions.find((x) => String(x.variantId) === String(id));
          subtotal += sug && sug.cents ? sug.cents : 0;
        });
        if (pairOn && v && v.cents) {
          root.querySelectorAll('[data-qa-save]').forEach((el) => {
            const key = el.dataset.qaSave;
            const sug = key === 'double' ? null : suggestions.find((x) => String(x.variantId) === key);
            const rowCents = key === 'double' ? v.cents : (sug && sug.cents) || 0;
            el.textContent = rowCents ? saveOn(v.cents + rowCents) : '';
          });
        }
        let after = 0;
        if (pairOn && n > 1 && subtotal > 0) {
          after = Math.round(subtotal * 0.95);
          total.innerHTML = `Total <strong>${money(after)}</strong> <s>${money(subtotal)}</s> &middot; 5% off applied at checkout`;
          total.hidden = false;
          addBtn.textContent = `Add ${n} cans \u00b7 ${money(after)}`;
        } else {
          total.hidden = true;
          addBtn.textContent = n > 1 ? `Add ${n} cans to cart` : 'Add to cart';
        }
        if (nudged && !name.value.trim()) addBtn.textContent = n > 1 ? `Add ${n} cans without a name${after ? ` \u00b7 ${money(after)}` : ''}` : 'Add without a name';
      };
      root.querySelectorAll('input[name="crc-qa-tier"]').forEach((input) => input.addEventListener('change', () => {
        const v = product.variants.find((x) => String(x.id) === input.value);
        if (doublePrice && v) doublePrice.textContent = v.price;
        if (help) help.textContent = helpFor(String(input.value) === String(stock && stock.id));
        clearError();
        syncButton();
      }));
      root.querySelectorAll('[data-qa-double], [data-qa-extra]').forEach((box) => box.addEventListener('change', () => { clearError(); syncButton(); }));
      syncButton();
      if (initial) addBtn.focus(); else root.querySelector('.crc-qa__x').focus();

      const showError = (message) => {
        error.textContent = message;
        error.hidden = false;
        addBtn.removeAttribute('aria-busy');
        syncButton();
      };

      addBtn.addEventListener('click', async () => {
        const picked = pickedTier();
        if (!picked || addBtn.getAttribute('aria-busy') === 'true') return;
        if (!syncNameError()) { name.focus(); return; }
        if (!name.value.trim() && !nudged && nudge) {
          // One reminder that the name is free; the next click adds the standard can.
          nudged = true;
          nudge.hidden = false;
          root.classList.add('is-nudged');
          syncNameError();
          syncButton();
          if (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
            nudge.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            name.focus({ preventScroll: true });
          } else {
            name.focus();
          }
          return;
        }
        error.hidden = true;
        addBtn.disabled = true;
        addBtn.setAttribute('aria-busy', 'true');
        addBtn.textContent = 'Adding to cart...';

        const chosen = Number(picked.value);
        const isStock = String(chosen) === String(stock && stock.id);
        const engraving = name.value.trim();
        const item = {
          id: chosen,
          quantity: doubleBox && doubleBox.checked ? 2 : 1,
          properties: { Package: isStock ? 'Stock-Up Can' : 'Range Day Can', Delivery: 'One-time purchase' }
        };
        if (engraving) {
          item.properties['Custom Name Engraving'] = engraving;
          item.properties['Engraving Placement'] = isStock ? 'Below the design on both cans' : 'Below We The People engraving';
        }
        // Suggested cans are Range Day tiers; the same name goes on each one.
        const extraItems = extras().map((id) => ({
          id,
          quantity: 1,
          properties: Object.assign(
            { Package: 'Range Day Can', Delivery: 'One-time purchase' },
            engraving ? { 'Custom Name Engraving': engraving, 'Engraving Placement': 'Below We The People engraving' } : {}
          )
        }));

        // Shipping insurance like the rest of the site: the line that fits the cart after this add, in the same request.
        const centsOf = (id) => {
          for (const p of (this.loadData().products || [])) {
            const v = (p.variants || []).find((x) => String(x.id) === String(id));
            if (v) return Number(v.cents) || 0;
          }
          return 0;
        };
        const addedCents = centsOf(chosen) * item.quantity + extraItems.reduce((sum, x) => sum + centsOf(x.id), 0);
        const RCU = window.RangeCanUpsell;
        const insurance = RCU && RCU.insuranceFor ? await RCU.insuranceFor(addedCents) : null;

        try {
          const response = await this.cartFetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item, ...extraItems, ...(insurance ? [insurance] : [])] })
          });
          if (!response.ok) {
            let message = 'We could not add this range can. Please try again.';
            try { const body = await response.json(); message = body.description || body.message || message; } catch (e) { /* keep default */ }
            showError(message);
            return;
          }
        } catch (e) {
          showError('Connection problem. Check your signal and try again.');
          return;
        }
        if (insurance && RCU.settleInsurance) await RCU.settleInsurance(insurance);

        let cans = cansAdding();
        try {
          const counts = await this.rangeCanCount();
          cans = counts.cans;
          this.updateCartBubble(counts.total);
        } catch (e) { /* show the confirmation anyway */ }

        const doneText = root.querySelector('[data-qa-done-text]');
        const offers = root.querySelector('[data-qa-offers]');
        const moreList = root.querySelector('[data-qa-more-list]');
        const checkoutLink = root.querySelector('[data-qa-checkout]');
        const pickedVariant = product.variants.find((x) => String(x.id) === String(chosen));
        const added = 1 + (doubleBox && doubleBox.checked ? 1 : 0) + extraItems.length;
        const doneTitle = root.querySelector('[data-qa-done-title]');
        doneTitle.textContent = `${shortTitle} ${isStock ? 'Stock-Up' : 'Range Day'}`;
        if (pairOn && cans >= 2) {
          doneText.innerHTML = `<strong>${cans} range cans in your cart.</strong> 5% comes off every one automatically at checkout.`;
          doneText.hidden = false;
        } else {
          doneText.hidden = true;
        }
        // Same offer sheet as the product pages (shared in range-can-upsell.js): Range Day buyers
        // see the Stock-Up upgrade first; with 2+ cans already in the cart only the upgrade remains.
        const U = window.RangeCanUpsell;
        const allRows = pairOn && U && U.offers ? this.offersFor(product, pickedVariant, isStock) : [];
        const rows = allRows;
        if (rows.length && moreList && offers) {
          const head = offers.querySelector('.crc-qa__offers-head');
          if (head && U.offersHead) head.innerHTML = U.offersHead(rows, cans);
          U.renderOffers(moreList, rows, { pickedVariant, engraving, cans, checkoutUrl: '/checkout' });
          offers.hidden = false;
          doneText.hidden = true;
        } else if (offers) {
          offers.hidden = true;
        }
        if (checkoutLink) checkoutLink.addEventListener('click', () => { checkoutLink.textContent = 'Going to checkout...'; });
        const engravedLine = root.querySelector('[data-qa-engraved]');
        const canCount = added + (isStock ? (doubleBox && doubleBox.checked ? 2 : 1) : 0);
        if (engraving) {
          engravedLine.innerHTML = `<strong>${esc(engraving)}</strong> on ${canCount === 1 ? 'your can' : `all ${canCount} cans`}. Check the spelling.`;
          engravedLine.hidden = false;
        } else {
          engravedLine.hidden = true;
        }
        root.querySelector('[data-qa-form]').hidden = true;
        root.querySelector('[data-qa-done]').hidden = false;
        doneTitle.focus();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.stuckObserver) this.stuckObserver.disconnect();
      if (this.headerSection) this.headerSection.removeEventListener('transitionend', this.syncTop);
    }

    headerHidesOnScroll() {
      if (this.stickyHeader && this.stickyHeader.hasAttribute('data-scroll-up')) return true;
      if (window.theme && window.theme.config && window.theme.config.mqlSmall) return true;
      return window.matchMedia('(max-width: 749px)').matches;
    }

    headerHeight() {
      const el = this.headerSection || this.stickyHeader;
      return el ? el.offsetHeight : 0;
    }

    // Header offset from its state classes, so it never lags behind the show/hide slide.
    headerBottom() {
      const el = this.headerSection;
      if (!el) return 0;
      const cls = el.classList;
      if (cls.contains('shopify-section-header-sticky')) {
        return cls.contains('shopify-section-header-hidden') ? 0 : el.offsetHeight;
      }
      const bottom = el.getBoundingClientRect().bottom;
      return bottom > 0 ? Math.round(bottom) : 0;
    }

    navOffset() {
      return this.headerBottom() + (this.nav ? this.nav.offsetHeight : 0);
    }

    syncStuck() {
      if (!this.nav) return;
      this.nav.classList.toggle('is-stuck', this.nav.getBoundingClientRect().top <= this.headerBottom() + 1);
    }

    syncTop() {
      this.syncStuck();
      const top = this.headerBottom();
      if (top === this.lastTop) return;
      this.lastTop = top;
      this.style.setProperty('--crc-sticky-top', `${top}px`);
      if (this.nav) this.style.setProperty('--crc-nav-h', `${this.nav.offsetHeight}px`);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.syncTop();

        const line = this.navOffset() + 40;
        let active = null;
        let best = -Infinity;
        this.groups.forEach((group) => {
          const top = Math.round(group.getBoundingClientRect().top);
          // Strictly greater: of several cards in the same row, the first one wins.
          if (top <= line && top > best) { best = top; active = group.id; }
        });
        if (this.pinned) {
          const pin = document.getElementById(this.pinned);
          const pinTop = pin ? Math.round(pin.getBoundingClientRect().top) : null;
          if (pinTop !== null && pinTop <= line && pinTop >= best - 2) active = this.pinned;
        }
        this.links.forEach((link) => {
          const on = link.dataset.crcNavLink === active;
          if (on === link.classList.contains('is-active')) return;
          link.classList.toggle('is-active', on);
          if (on) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
          if (on && this.chips) {
            // First chip pins to the start so its 16px gutter never scrolls away.
            const left = link === this.links[0] ? 0 : link.offsetLeft - (this.chips.clientWidth - link.offsetWidth) / 2;
            this.chips.scrollTo({ left, behavior: 'smooth' });
          }
        });
      });
    }
  });
})();
